import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Alert,
  Tag,
  Tooltip,
  InputNumber,
  Select,
  Checkbox,
  Spin,
  Divider,
  Row,
  Col,
  Modal,
  message,
  Image,
  Input
} from 'antd';
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  CameraOutlined
} from '@ant-design/icons';
import { getMaterialListByInvoice } from '../../firebase/materialListService';
import { getInvoiceById } from '../../firebase/invoiceService';
import { updateInventoryItemCondition } from '../../firebase/inventoryService';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Damage severity levels
export const DAMAGE_SEVERITY = {
  MINOR: 'minor',
  MODERATE: 'moderate',
  SEVERE: 'severe',
  TOTAL_LOSS: 'total_loss'
};

export const DAMAGE_SEVERITY_LABELS = {
  [DAMAGE_SEVERITY.MINOR]: 'Minor Damage',
  [DAMAGE_SEVERITY.MODERATE]: 'Moderate Damage', 
  [DAMAGE_SEVERITY.SEVERE]: 'Severe Damage',
  [DAMAGE_SEVERITY.TOTAL_LOSS]: 'Total Loss'
};

export const DAMAGE_SEVERITY_COLORS = {
  [DAMAGE_SEVERITY.MINOR]: '#faad14',      // Orange
  [DAMAGE_SEVERITY.MODERATE]: '#ff7a45',   // Orange-Red
  [DAMAGE_SEVERITY.SEVERE]: '#ff4d4f',     // Red
  [DAMAGE_SEVERITY.TOTAL_LOSS]: '#722ed1'  // Purple
};

// Common damage types
export const DAMAGE_TYPES = {
  BENT: 'bent',
  CRACKED: 'cracked',
  BROKEN: 'broken',
  MISSING: 'missing',
  CORRODED: 'corroded',
  WORN: 'worn',
  CONTAMINATED: 'contaminated',
  OTHER: 'other'
};

export const DAMAGE_TYPE_LABELS = {
  [DAMAGE_TYPES.BENT]: 'Bent/Deformed',
  [DAMAGE_TYPES.CRACKED]: 'Cracked',
  [DAMAGE_TYPES.BROKEN]: 'Broken',
  [DAMAGE_TYPES.MISSING]: 'Missing',
  [DAMAGE_TYPES.CORRODED]: 'Corroded/Rusted',
  [DAMAGE_TYPES.WORN]: 'Excessive Wear',
  [DAMAGE_TYPES.CONTAMINATED]: 'Contaminated',
  [DAMAGE_TYPES.OTHER]: 'Other'
};

/**
 * Damaged Item Selector Component
 * Handles selection and documentation of damaged items from original rental
 */
const DamagedItemSelector = ({
  originalInvoiceId,
  originalInvoiceNumber,
  onDamagedItemsChange,
  readOnly = false,
  showTitle = true
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [originalMaterialList, setOriginalMaterialList] = useState(null);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedDamagedItems, setSelectedDamagedItems] = useState([]);
  const [damageDetailsModal, setDamageDetailsModal] = useState({ visible: false, item: null });
  const [photoModal, setPhotoModal] = useState({ visible: false, photos: [] });

  // Load original invoice and material list
  useEffect(() => {
    if (!originalInvoiceId) return;

    loadOriginalInvoiceData();
  }, [originalInvoiceId]);

  const loadOriginalInvoiceData = async () => {
    setLoading(true);
    try {
      console.log(`📋 Loading original invoice data: ${originalInvoiceId}`);

      // Load original invoice
      const invoice = await getInvoiceById(originalInvoiceId);
      if (!invoice) {
        message.error('Original invoice not found');
        return;
      }
      setOriginalInvoice(invoice);

      // Load material list for original invoice
      const materialList = await getMaterialListByInvoice(originalInvoiceId);
      if (!materialList) {
        console.warn('⚠️ No material list found for original invoice');
        setAvailableItems([]);
        return;
      }
      setOriginalMaterialList(materialList);

      // Convert material requirements to selectable items
      const items = convertMaterialRequirementsToItems(materialList.requirements);
      setAvailableItems(items);

      console.log(`✅ Loaded ${items.length} items from original invoice`);

    } catch (error) {
      console.error('❌ Error loading original invoice data:', error);
      message.error('Failed to load original invoice data');
    } finally {
      setLoading(false);
    }
  };

  // Convert material requirements to individual selectable items
  const convertMaterialRequirementsToItems = (requirements) => {
    const items = [];
    let itemId = 1;

    Object.entries(requirements).forEach(([materialType, requirement]) => {
      // Create individual items based on quantity
      for (let i = 0; i < requirement.totalQuantity; i++) {
        items.push({
          id: itemId++,
          materialType,
          itemNumber: i + 1,
          description: `${materialType} #${i + 1}`,
          originalQuantity: requirement.totalQuantity,
          condition: 'used', // Assuming items were used in rental
          estimatedValue: calculateEstimatedValue(materialType),
          configurations: requirement.configurations || [],
          isDamaged: false,
          damageDetails: null
        });
      }
    });

    return items;
  };

  // Calculate estimated value for material type
  const calculateEstimatedValue = (materialType) => {
    // This would typically come from inventory data
    // For now, use rough estimates
    const estimates = {
      'Scaffold Frames': 150,
      'Cross Braces': 50,
      'Base Plates': 80,
      'Adjustable Legs': 120,
      'Planks': 100,
      'Guardrails': 90,
      'Toe Boards': 60,
      'Couplers': 25,
      'Pins': 15,
      'Clips': 10
    };

    // Find matching estimate or use default
    const matchingKey = Object.keys(estimates).find(key => 
      materialType.toLowerCase().includes(key.toLowerCase())
    );

    return matchingKey ? estimates[matchingKey] : 100;
  };

  // Handle item selection for damage
  const handleItemSelection = useCallback((itemId, selected) => {
    if (readOnly) return;

    setAvailableItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, isDamaged: selected, damageDetails: selected ? {} : null }
        : item
    ));

    // Update selected damaged items
    if (selected) {
      const item = availableItems.find(i => i.id === itemId);
      if (item) {
        setSelectedDamagedItems(prev => [...prev, { ...item, isDamaged: true }]);
      }
    } else {
      setSelectedDamagedItems(prev => prev.filter(item => item.id !== itemId));
    }
  }, [availableItems, readOnly]);

  // Handle damage details update
  const handleDamageDetailsUpdate = useCallback((itemId, damageDetails) => {
    setAvailableItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, damageDetails }
        : item
    ));

    setSelectedDamagedItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, damageDetails }
        : item
    ));

    // Trigger callback to parent
    const updatedItems = selectedDamagedItems.map(item => 
      item.id === itemId ? { ...item, damageDetails } : item
    );
    
    if (onDamagedItemsChange) {
      onDamagedItemsChange(updatedItems);
    }
  }, [selectedDamagedItems, onDamagedItemsChange]);

  // Open damage details modal
  const openDamageDetailsModal = useCallback((item) => {
    setDamageDetailsModal({ visible: true, item });
  }, []);

  // Close damage details modal
  const closeDamageDetailsModal = useCallback(() => {
    setDamageDetailsModal({ visible: false, item: null });
  }, []);

  // Save damage details
  const saveDamageDetails = useCallback((damageDetails) => {
    if (damageDetailsModal.item) {
      handleDamageDetailsUpdate(damageDetailsModal.item.id, damageDetails);
      closeDamageDetailsModal();
      message.success('Damage details saved');
    }
  }, [damageDetailsModal.item, handleDamageDetailsUpdate, closeDamageDetailsModal]);

  // Calculate total damage value
  const totalDamageValue = useMemo(() => {
    return selectedDamagedItems.reduce((total, item) => {
      const damageMultiplier = getDamageValueMultiplier(item.damageDetails?.severity);
      return total + (item.estimatedValue * damageMultiplier);
    }, 0);
  }, [selectedDamagedItems]);

  // Get damage value multiplier based on severity
  const getDamageValueMultiplier = (severity) => {
    const multipliers = {
      [DAMAGE_SEVERITY.MINOR]: 0.2,
      [DAMAGE_SEVERITY.MODERATE]: 0.5,
      [DAMAGE_SEVERITY.SEVERE]: 0.8,
      [DAMAGE_SEVERITY.TOTAL_LOSS]: 1.0
    };
    return multipliers[severity] || 0.5;
  };

  // Table columns for available items
  const columns = [
    {
      title: 'Select',
      dataIndex: 'isDamaged',
      key: 'select',
      width: 60,
      render: (isDamaged, record) => (
        <Checkbox
          checked={isDamaged}
          onChange={(e) => handleItemSelection(record.id, e.target.checked)}
          disabled={readOnly}
        />
      )
    },
    {
      title: 'Item',
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Type: {record.materialType}
          </div>
        </div>
      )
    },
    {
      title: 'Condition',
      dataIndex: 'condition',
      key: 'condition',
      width: 100,
      render: (condition) => (
        <Tag color={condition === 'new' ? 'green' : 'blue'}>
          {condition.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Est. Value',
      dataIndex: 'estimatedValue',
      key: 'estimatedValue',
      width: 100,
      render: (value) => `$${value}`
    },
    {
      title: 'Damage Details',
      key: 'damageDetails',
      width: 150,
      render: (_, record) => {
        if (!record.isDamaged) {
          return <Text type="secondary">-</Text>;
        }

        const hasDetails = record.damageDetails && Object.keys(record.damageDetails).length > 0;
        
        return (
          <Space>
            <Button
              size="small"
              type={hasDetails ? 'primary' : 'default'}
              icon={hasDetails ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
              onClick={() => openDamageDetailsModal(record)}
              disabled={readOnly}
            >
              {hasDetails ? 'Edit' : 'Add'} Details
            </Button>
            {hasDetails && record.damageDetails.severity && (
              <Tag color={DAMAGE_SEVERITY_COLORS[record.damageDetails.severity]}>
                {DAMAGE_SEVERITY_LABELS[record.damageDetails.severity]}
              </Tag>
            )}
          </Space>
        );
      }
    }
  ];

  // Don't render if no original invoice ID
  if (!originalInvoiceId) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <ExclamationCircleOutlined style={{ fontSize: '48px', color: '#faad14' }} />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              Please select an original rental invoice to view available items.
            </Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={showTitle ? "Damaged Items Selection" : null}
      style={{ marginBottom: 16 }}
    >
      {/* Original Invoice Information */}
      {originalInvoice && (
        <Alert
          message={
            <Space>
              <InfoCircleOutlined />
              <Text strong>Original Rental Invoice: {originalInvoiceNumber}</Text>
            </Space>
          }
          description={
            <div>
              <Text>Customer: {originalInvoice.companyName}</Text>
              <br />
              <Text>Site: {originalInvoice.siteAddress || 'Not specified'}</Text>
              <br />
              <Text>Total Items Available: {availableItems.length}</Text>
            </div>
          }
          type="info"
          showIcon={false}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Loading original invoice items...</Text>
          </div>
        </div>
      )}

      {/* Items Selection Table */}
      {!loading && availableItems.length > 0 && (
        <>
          <Table
            columns={columns}
            dataSource={availableItems}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: true }}
          />

          {/* Summary */}
          {selectedDamagedItems.length > 0 && (
            <>
              <Divider />
              <Row gutter={16}>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">Items Selected</Text>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      {selectedDamagedItems.length}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">Total Damage Value</Text>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff4d4f' }}>
                      ${totalDamageValue.toFixed(2)}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">Items with Details</Text>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                      {selectedDamagedItems.filter(item => 
                        item.damageDetails && Object.keys(item.damageDetails).length > 0
                      ).length}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      {/* No Items Message */}
      {!loading && availableItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <WarningOutlined style={{ fontSize: '48px', color: '#faad14' }} />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              No items found in the original rental invoice. Please verify the invoice number.
            </Text>
          </div>
        </div>
      )}

      {/* Damage Details Modal */}
      <DamageDetailsModal
        visible={damageDetailsModal.visible}
        item={damageDetailsModal.item}
        onSave={saveDamageDetails}
        onCancel={closeDamageDetailsModal}
      />
    </Card>
  );
};

/**
 * Damage Details Modal Component
 */
const DamageDetailsModal = ({ visible, item, onSave, onCancel }) => {
  const [form, setForm] = useState({
    severity: DAMAGE_SEVERITY.MODERATE,
    damageType: DAMAGE_TYPES.OTHER,
    description: '',
    photos: [],
    repairCost: 0,
    replacementNeeded: false
  });

  // Initialize form when item changes
  useEffect(() => {
    if (item && item.damageDetails) {
      setForm({ ...form, ...item.damageDetails });
    } else if (item) {
      setForm({
        severity: DAMAGE_SEVERITY.MODERATE,
        damageType: DAMAGE_TYPES.OTHER,
        description: '',
        photos: [],
        repairCost: 0,
        replacementNeeded: false
      });
    }
  }, [item]);

  const handleSave = () => {
    if (!form.description.trim()) {
      message.error('Please provide a damage description');
      return;
    }

    onSave(form);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal
      title={`Damage Details - ${item?.description}`}
      visible={visible}
      onOk={handleSave}
      onCancel={onCancel}
      width={600}
      okText="Save Details"
    >
      {item && (
        <div>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Damage Severity</Text>
                <Select
                  value={form.severity}
                  onChange={(value) => handleFormChange('severity', value)}
                  style={{ width: '100%', marginTop: 4 }}
                >
                  {Object.entries(DAMAGE_SEVERITY_LABELS).map(([key, label]) => (
                    <Option key={key} value={key}>
                      <Tag color={DAMAGE_SEVERITY_COLORS[key]} style={{ marginRight: 8 }}>
                        {label}
                      </Tag>
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Damage Type</Text>
                <Select
                  value={form.damageType}
                  onChange={(value) => handleFormChange('damageType', value)}
                  style={{ width: '100%', marginTop: 4 }}
                >
                  {Object.entries(DAMAGE_TYPE_LABELS).map(([key, label]) => (
                    <Option key={key} value={key}>{label}</Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Description</Text>
            <TextArea
              value={form.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Describe the damage in detail..."
              rows={3}
              style={{ marginTop: 4 }}
            />
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Estimated Repair Cost</Text>
                <InputNumber
                  value={form.repairCost}
                  onChange={(value) => handleFormChange('repairCost', value || 0)}
                  min={0}
                  precision={2}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 16, marginTop: 24 }}>
                <Checkbox
                  checked={form.replacementNeeded}
                  onChange={(e) => handleFormChange('replacementNeeded', e.target.checked)}
                >
                  Replacement Required
                </Checkbox>
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Photos</Text>
            <div style={{ marginTop: 8, padding: '20px', border: '1px dashed #d9d9d9', borderRadius: '6px', textAlign: 'center' }}>
              <CameraOutlined style={{ fontSize: '24px', color: '#d9d9d9' }} />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Photo upload functionality would be implemented here</Text>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DamagedItemSelector;
